import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { userService } from './user.service';
import { handleAuthError } from './auth';
import { platformClient } from '../api';
import { CurrentUser } from '../api/types';


interface UserContextValue {
  user: CurrentUser | null;
  loading: boolean;
  error: Error | null;
  refetchUser: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export interface UserProviderProps {
  children: ReactNode;
}

// LocalStorage key for user cache (display-only, never used for access decisions)
const USER_CACHE_KEY = 'core_user_cache';

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const hasFetchedRef = useRef(false);
  const fetchingRef = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  const fetchUser = async (force = false) => {
    if (fetchingRef.current) return;
    if (hasFetchedRef.current && !force) return;
    hasFetchedRef.current = true;
    fetchingRef.current = true;
    
    try {
      if (!userRef.current) setLoading(true);
      setError(null);
      
      const response = await userService.getCurrentUser();
      
      if (response.error) {
        handleAuthError(response.response);
        const err = response.error as any;
        const errorMessage = err.detail?.[0]?.msg || 'Authentication failed';
        throw new Error(errorMessage);
      }
      
      if (response.data) {
        const serialized = JSON.stringify(response.data);
        // Only update state if data actually changed to prevent re-render cascade
        // (visibilitychange refetch often returns identical data)
        if (serialized !== JSON.stringify(userRef.current)) {
          setUser(response.data);
        }
        try {
          localStorage.setItem(USER_CACHE_KEY, serialized);
        } catch {
          // Ignore cache write failures
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user'));
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  // Refetch when tab regains focus to pick up role/permission changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hasFetchedRef.current) {
        fetchUser(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const value: UserContextValue = {
    user,
    loading,
    error,
    refetchUser: () => fetchUser(true),
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}


export async function fetchUser(): Promise<CurrentUser> {
  const response = await platformClient.GET('/v1/auth/users/me', {
    credentials: 'include',
  });
  if (response.response?.status === 200) {
    return response.data as CurrentUser;
  } 

  if (response.response?.status === 401) {
    throw new Error('Unauthorized');
  }

  const err = response.error as any;
  if (err.detail && typeof err.detail === 'string') {
    throw new Error(err.detail);
  }

  if (err.detail && typeof err.detail === 'object') {
    throw new Error(err.detail[0].msg);
  }

  throw new Error('Failed to fetch logged in user. Please login again.');
}
